import {
  IonButton,
  IonContent,
  IonIcon,
  IonImg,
  IonMenuToggle,
  IonPage,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import { chevronDownOutline, chevronForwardOutline, ellipsisVertical, refreshCircleOutline, searchOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import {
  CasinoService,
  IManufacturersManagementResponse,
  ISetSlotcheckManufacturerResponse,
  IUnsetSlotcheckManufacturerResponse,
  IUnmappedManufacturerRow,
} from 'services/casino';
import {
  MANUFACTURERS_MANAGEMENT_GROUP,
  canAccessManufacturersManagement,
} from './manufacturersManagementAccess';
import {
  filterSortManaged,
  filterSortUnmapped,
  mappedFromForRow,
  setManufacturerConfirmMessage,
  SortKey,
  toggleExpandedGroup,
  unmapManufacturerConfirmMessage,
} from './manufacturersManagementUi';
import { refreshKeepingIonScroll } from './egmsAdminAfterSet';
import { buildAdminConfirmAlertOptions } from 'components/AdminConfirmAlert/adminConfirmAlert';

type TabId = 'managed' | 'unmapped';

const MenuKebab = () => (
  <div className="manufacturers-mgmt__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="manufacturers-mgmt__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="manufacturers-mgmt__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent manufacturers-mgmt">
    <IonContent className="manufacturers-mgmt">
      <MenuKebab />
      <div className="manufacturers-mgmt__content">
        <div className="manufacturers-mgmt__breadcrumb-row">
          <IonMenuToggle menu="main">
            <IonButton fill="clear" className="manufacturers-mgmt__back" aria-label="Open menu">
              <IonImg src="assets/images/back-icon.svg" />
            </IonButton>
          </IonMenuToggle>
          <div className="manufacturers-mgmt__breadcrumb">
            <span>Manufacturers Management</span>
          </div>
        </div>
        <div className="manufacturers-mgmt__forbidden-card">
          <h2>403 · Manufacturers Management not enabled</h2>
          <p>
            Requires Cognito group <strong>{MANUFACTURERS_MANAGEMENT_GROUP}</strong>. Ask ops to
            create and assign it in the Cognito user pool (dev and prod{' '}
            <code>us-east-1_wixKjr4v5</code>).
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

export const ManufacturersManagementPage = () => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = canAccessManufacturersManagement(groups);

  const [tab, setTab] = useState<TabId>('unmapped');
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [payload, setPayload] = useState<IManufacturersManagementResponse | null>(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [draftScByMfr, setDraftScByMfr] = useState<Record<string, string>>({});
  const [busyMfr, setBusyMfr] = useState<string | null>(null);
  /** Multi-open accordion keys = slotcheckManufacturer names. */
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());
  const [busyUnmapKey, setBusyUnmapKey] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const contentRef = useRef<HTMLIonContentElement | null>(null);

  const managedOptions = useMemo(
    () => (payload?.managed || []).map((m) => m.slotcheckManufacturer),
    [payload]
  );

  const load = useCallback(
    async (opts?: { fromRefresh?: boolean }) => {
      if (!canAccess) return;
      const seq = ++loadSeq.current;
      if (opts?.fromRefresh) setRefreshBusy(true);
      else setPhase('loading');
      try {
        const res = await firstValueFrom(casinoSvc.GetManufacturersManagement());
        if (seq !== loadSeq.current) return;
        setPayload(res && typeof res === 'object' ? res : null);
        setPhase('ready');
      } catch (err: unknown) {
        if (seq !== loadSeq.current) return;
        setPayload(null);
        setPhase('ready');
        const status = (err as { response?: { status?: number }; status?: number })?.response
          ?.status || (err as { status?: number })?.status;
        if (status !== 403) {
          void presentToast({
            position: 'top',
            duration: 4000,
            color: 'danger',
            message: 'Failed to load Manufacturers Management.',
          });
        }
      } finally {
        if (seq === loadSeq.current) setRefreshBusy(false);
      }
    },
    [canAccess, casinoSvc, presentToast]
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Drop expanded keys for groups that disappeared after Unmap / refresh.
  useEffect(() => {
    const names = new Set((payload?.managed || []).map((m) => m.slotcheckManufacturer));
    setExpandedGroups((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((n) => {
        if (names.has(n)) next.add(n);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [payload]);

  const managedRows = useMemo(
    () => filterSortManaged(payload?.managed || [], query, sortKey, sortDir),
    [payload, query, sortKey, sortDir]
  );
  const unmappedRows = useMemo(
    () => filterSortUnmapped(payload?.unmapped || [], query, sortKey, sortDir),
    [payload, query, sortKey, sortDir]
  );

  const confirmUnmap = (
    slotcheckManufacturer: string,
    raw: { manufacturer: string; egmCount: number }
  ) => {
    const unmapKey = `${slotcheckManufacturer}\0${raw.manufacturer}`;
    void presentAlert(
      buildAdminConfirmAlertOptions({
        header: 'Unmap manufacturer',
        message: unmapManufacturerConfirmMessage(
          raw.manufacturer,
          slotcheckManufacturer,
          raw.egmCount
        ),
        confirmText: 'Unmap',
        onConfirm: () => {
          void (async () => {
            setBusyUnmapKey(unmapKey);
            try {
              const res = (await firstValueFrom(
                casinoSvc.UnsetSlotcheckManufacturer({
                  manufacturer: raw.manufacturer,
                  slotcheckManufacturer,
                })
              )) as IUnsetSlotcheckManufacturerResponse;
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'success',
                message: `Unmapped ${res?.modifiedCount ?? 0} of ${
                  res?.matchedCount ?? 0
                } matched EGMs.`,
              });
              // Stay on SlotCheck Manufacturers; keep search/sort/expanded; refresh lists.
              await refreshKeepingIonScroll(contentRef.current, () =>
                load({ fromRefresh: true })
              );
            } catch {
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'danger',
                message: 'Unmap failed.',
              });
            } finally {
              setBusyUnmapKey(null);
            }
          })();
        },
      })
    );
  };

  const confirmSet = (row: IUnmappedManufacturerRow) => {
    const draft = (draftScByMfr[row.manufacturer] || '').trim();
    if (!draft) {
      void presentToast({
        position: 'top',
        duration: 3000,
        color: 'warning',
        message: 'Enter or select a SlotCheck manufacturer value.',
      });
      return;
    }
    void presentAlert(
      buildAdminConfirmAlertOptions({
        header: 'Set slotcheckManufacturer',
        message: setManufacturerConfirmMessage(row.manufacturer, draft, row.egmCount),
        onConfirm: () => {
          void (async () => {
            setBusyMfr(row.manufacturer);
            try {
              const res = (await firstValueFrom(
                casinoSvc.SetSlotcheckManufacturer({
                  manufacturer: row.manufacturer,
                  slotcheckManufacturer: draft,
                })
              )) as ISetSlotcheckManufacturerResponse;
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'success',
                message: `Updated ${res?.modifiedCount ?? 0} of ${
                  res?.matchedCount ?? 0
                } matched EGMs.`,
              });
              setDraftScByMfr((prev) => {
                const next = { ...prev };
                delete next[row.manufacturer];
                return next;
              });
              // Stay on current tab (Unmapped); keep search/sort; refresh so the
              // mapped row leaves Unmapped and both tab counts update. Toast only —
              // never auto-switch to the SlotCheck Manufacturers tab.
              await refreshKeepingIonScroll(contentRef.current, () =>
                load({ fromRefresh: true })
              );
            } catch {
              void presentToast({
                position: 'top',
                duration: 4000,
                color: 'danger',
                message: 'Update failed.',
              });
            } finally {
              setBusyMfr(null);
            }
          })();
        },
      })
    );
  };

  if (!canAccess) return <ForbiddenCard />;

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  return (
    <IonPage className="app-parent manufacturers-mgmt">
      <IonContent ref={contentRef} className="manufacturers-mgmt">
        <MenuKebab />
        <div className="manufacturers-mgmt__content">
          <div className="manufacturers-mgmt__breadcrumb-row">
            <IonMenuToggle menu="main">
              <IonButton fill="clear" className="manufacturers-mgmt__back" aria-label="Open menu">
                <IonImg src="assets/images/back-icon.svg" />
              </IonButton>
            </IonMenuToggle>
            <div className="manufacturers-mgmt__breadcrumb">
              <span>Manufacturers Management</span>
            </div>
          </div>

          <div className="manufacturers-mgmt__header">
            <div className="manufacturers-mgmt__title-row">
              <h1 className="manufacturers-mgmt__title">Manufacturers Management</h1>
              <IonButton
                className="manufacturers-mgmt__refresh"
                fill="solid"
                disabled={refreshBusy || phase === 'loading'}
                onClick={() => void load({ fromRefresh: true })}
              >
                <IonIcon icon={refreshCircleOutline} slot="start" />
                {refreshBusy ? 'Refreshing…' : 'Refresh'}
              </IonButton>
            </div>
            <p className="manufacturers-mgmt__subtitle">
              Distinct manufacturers from insight <code>machines</code> across all casinoIds.
              Only <code>slotcheckManufacturer</code> is written — raw <code>manufacturer</code>{' '}
              is never changed.
            </p>
          </div>

          <div className="manufacturers-mgmt__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'managed'}
              className={
                tab === 'managed'
                  ? 'manufacturers-mgmt__tab manufacturers-mgmt__tab--active'
                  : 'manufacturers-mgmt__tab'
              }
              onClick={() => setTab('managed')}
            >
              SlotCheck Manufacturers
              <span className="manufacturers-mgmt__tab-count">{managedRows.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'unmapped'}
              className={
                tab === 'unmapped'
                  ? 'manufacturers-mgmt__tab manufacturers-mgmt__tab--active'
                  : 'manufacturers-mgmt__tab'
              }
              onClick={() => setTab('unmapped')}
            >
              Unmapped Manufacturers
              <span className="manufacturers-mgmt__tab-count">{unmappedRows.length}</span>
            </button>
          </div>

          <div className="manufacturers-mgmt__toolbar">
            <div className="manufacturers-mgmt__search">
              <IonIcon icon={searchOutline} />
              <input
                className="manufacturers-mgmt__filter-input"
                type="search"
                value={query}
                placeholder="Search manufacturers…"
                aria-label="Search manufacturers"
                autoComplete="off"
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="manufacturers-mgmt__sort">
              <button type="button" onClick={() => toggleSort('name')}>
                Name{sortKey === 'name' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
              <button type="button" onClick={() => toggleSort('egmCount')}>
                EGMs{sortKey === 'egmCount' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
              <button type="button" onClick={() => toggleSort('activeCount')}>
                Active{sortKey === 'activeCount' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
              </button>
            </div>
          </div>

          {phase === 'loading' && !payload ? (
            <p className="manufacturers-mgmt__loading">Loading…</p>
          ) : tab === 'managed' ? (
            <section className="manufacturers-mgmt__card" data-testid="managed-list">
              {managedRows.length === 0 ? (
                <p className="manufacturers-mgmt__empty">No managed manufacturers.</p>
              ) : (
                <ul className="manufacturers-mgmt__list">
                  {managedRows.map((row) => {
                    const mapped = mappedFromForRow(row);
                    const open = expandedGroups.has(row.slotcheckManufacturer);
                    return (
                      <li
                        key={row.slotcheckManufacturer}
                        className={
                          open
                            ? 'manufacturers-mgmt__row manufacturers-mgmt__row--expandable manufacturers-mgmt__row--open'
                            : 'manufacturers-mgmt__row manufacturers-mgmt__row--expandable'
                        }
                      >
                        <button
                          type="button"
                          className="manufacturers-mgmt__row-toggle"
                          aria-expanded={open}
                          onClick={() =>
                            setExpandedGroups((prev) =>
                              toggleExpandedGroup(prev, row.slotcheckManufacturer)
                            )
                          }
                        >
                          <IonIcon
                            icon={open ? chevronDownOutline : chevronForwardOutline}
                            className="manufacturers-mgmt__chevron"
                            aria-hidden="true"
                          />
                          <div className="manufacturers-mgmt__row-main">
                            <div className="manufacturers-mgmt__name">
                              {row.slotcheckManufacturer}
                            </div>
                            {mapped.length > 0 ? (
                              <div className="manufacturers-mgmt__mapped">
                                Maps from: {mapped.map((m) => m.manufacturer).join(', ')}
                              </div>
                            ) : null}
                          </div>
                          <div className="manufacturers-mgmt__counts">
                            <span>
                              <strong>{row.egmCount.toLocaleString()}</strong> EGMs
                            </span>
                            <span className="manufacturers-mgmt__muted">
                              {row.activeCount.toLocaleString()} active
                            </span>
                          </div>
                        </button>
                        {open ? (
                          <ul className="manufacturers-mgmt__sublist" data-testid="mapped-raw-list">
                            {mapped.map((raw) => {
                              const unmapKey = `${row.slotcheckManufacturer}\0${raw.manufacturer}`;
                              return (
                                <li
                                  key={raw.manufacturer}
                                  className="manufacturers-mgmt__subrow"
                                >
                                  <div className="manufacturers-mgmt__subrow-main">
                                    <div className="manufacturers-mgmt__subname">
                                      {raw.manufacturer}
                                    </div>
                                    <div className="manufacturers-mgmt__counts manufacturers-mgmt__counts--inline">
                                      <span>
                                        <strong>{raw.egmCount.toLocaleString()}</strong> EGMs
                                      </span>
                                      <span className="manufacturers-mgmt__muted">
                                        {raw.activeCount.toLocaleString()} active
                                      </span>
                                    </div>
                                  </div>
                                  <IonButton
                                    size="small"
                                    fill="outline"
                                    className="manufacturers-mgmt__unmap"
                                    disabled={busyUnmapKey === unmapKey}
                                    onClick={() =>
                                      confirmUnmap(row.slotcheckManufacturer, raw)
                                    }
                                  >
                                    {busyUnmapKey === unmapKey ? 'Unmapping…' : 'Unmap'}
                                  </IonButton>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          ) : (
            <section className="manufacturers-mgmt__card" data-testid="unmapped-list">
              {unmappedRows.length === 0 ? (
                <p className="manufacturers-mgmt__empty">No unmapped manufacturers.</p>
              ) : (
                <ul className="manufacturers-mgmt__list">
                  {unmappedRows.map((row) => (
                    <li key={row.manufacturer} className="manufacturers-mgmt__row manufacturers-mgmt__row--action">
                      <div className="manufacturers-mgmt__row-main">
                        <div className="manufacturers-mgmt__name">{row.manufacturer}</div>
                        <div className="manufacturers-mgmt__counts manufacturers-mgmt__counts--inline">
                          <span>
                            <strong>{row.egmCount.toLocaleString()}</strong> EGMs
                          </span>
                          <span className="manufacturers-mgmt__muted">
                            {row.activeCount.toLocaleString()} active
                          </span>
                        </div>
                      </div>
                      <div className="manufacturers-mgmt__set">
                        <input
                          className="manufacturers-mgmt__input"
                          list="mm-managed-options"
                          placeholder="SlotCheck manufacturer…"
                          value={draftScByMfr[row.manufacturer] || ''}
                          onChange={(e) =>
                            setDraftScByMfr((prev) => ({
                              ...prev,
                              [row.manufacturer]: e.target.value,
                            }))
                          }
                          disabled={busyMfr === row.manufacturer}
                        />
                        <IonButton
                          size="small"
                          fill="solid"
                          disabled={busyMfr === row.manufacturer}
                          onClick={() => confirmSet(row)}
                        >
                          {busyMfr === row.manufacturer ? 'Updating…' : 'Set'}
                        </IonButton>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <datalist id="mm-managed-options">
                {managedOptions.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </section>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ManufacturersManagementPage;
